const EDITORIAL_SUBJECT_ASSESSMENT =
  /^\s*(?:this|the)\s+(?:dialogue(?:\s+exchange)?|passage|paragraph|scene|section|beat|transition)\b[\s\S]{0,180}\b(?:feels?|seems?|reads?|appears?|is)\b[\s\S]{0,120}\b(?:out of (?:sequence|place)|disconnected|repetitive|abrupt|unclear|unnecessary|too (?:long|short|slow|fast)|needs?\b|would (?:work|fit|flow))\b/i;

const EDITORIAL_ACTION =
  /(?:^|[.!?]\s+)(?:consider|try)\s+(?:integrating|moving|adding|removing|cutting|rewriting|reordering|splitting|merging|combining|placing|expanding|condensing|shortening|tightening)\b[\s\S]{0,180}\b(?:passage|paragraph|scene|section|beat|dialogue|exchange|transition|moment|pacing|sequence)\b/i;

/**
 * Detect explicit editorial coaching that cannot safely replace manuscript prose.
 * Keep this intentionally narrow: ordinary imperatives and words such as "scene"
 * can be valid narrative text, while these assessment/action combinations refer
 * to the manuscript as an object being edited.
 */
export function feedbackSuggestionLooksLikeEditorialAdvice(
  suggestion: string | null | undefined,
): boolean {
  const value = String(suggestion ?? "").trim();
  if (!value) return false;
  return EDITORIAL_SUBJECT_ASSESSMENT.test(value) || EDITORIAL_ACTION.test(value);
}

// ---------------------------------------------------------------------------
// SW-10283: Detect when a suggestion contradicts its own comment
// ---------------------------------------------------------------------------

const PUNCTUATION_CLAIM_MAP: Array<{ keyword: RegExp; chars: RegExp }> = [
  { keyword: /\bcommas?\b/i, chars: /,/g },
  { keyword: /\bsemicolons?\b/i, chars: /;/g },
  { keyword: /\bperiods?\b|\bfull\s*stops?\b/i, chars: /\./g },
  { keyword: /\bapostrophes?\b/i, chars: /['‘’]/g },
  { keyword: /\bcolons?\b/i, chars: /:/g },
  { keyword: /\bem[\s-]?dash/i, chars: /—/g },
  { keyword: /\ben[\s-]?dash/i, chars: /–/g },
  { keyword: /\bdash(?:es)?\b|\bhyphens?\b/i, chars: /[-—–]/g },
  { keyword: /\bellips[ei]s/i, chars: /…|\.\.\./g },
  { keyword: /\bquestion\s*marks?\b/i, chars: /\?/g },
  { keyword: /\bexclamation\s*(?:marks?|points?)\b/i, chars: /!/g },
  { keyword: /\b(?:quote|quotation)\s*marks?\b/i, chars: /["“”'‘’]/g },
];

const ALL_PUNCTUATION_PATTERN = /[,;:.!?\-—–…'‘’"“”]/g;

const REMOVAL_CLAIM =
  /\b(?:remove|cut|delete|omit|drop|eliminate)\b[\s\S]{0,60}\b(?:repeated|redundant|unnecessary|duplicate|extra|extraneous)\b/i;

const MOVE_CLAIM =
  /\b(?:move|relocate|place|reposition)\b[\s\S]{0,60}\b(?:earlier|later|before|after|above|below|preceding|following)\b/i;

const MOVE_PUNCTUATION =
  /\b(?:move|relocate|place|reposition)\s+(?:(?:the|a|an|this|that|these|those)\s+)?(?:(?:em|en)[\s-]?)?(?:comma|semicolon|period|colon|dash|hyphen|apostrophe|ellipsis|question\s*mark|exclamation|quote|quotation)/i;

const UNNEEDED_PUNCTUATION =
  /\b(?:(?:not|never|no longer|isn['’]t|aren['’]t)\s+(?:needed|necessary|required)|unnecessary|redundant|extra)\b/i;

const SAID_BOOKISMS = new Set([
  "barked", "bellowed", "boomed", "breathed", "choked", "cooed", "cried",
  "croaked", "declared", "demanded", "drawled", "exclaimed", "gasped",
  "groaned", "growled", "hissed", "howled", "interjected", "moaned",
  "murmured", "muttered", "panted", "pleaded", "proclaimed", "purred",
  "rasped", "remarked", "retorted", "roared", "sighed", "snarled",
  "snapped", "sobbed", "squeaked", "stammered", "thundered", "wailed",
  "whimpered", "whispered", "yelled",
]);

const SAID_BOOKISM_CLAIM =
  /\bsaid[\s-]?bookism\b|\b(?:simpler|plain|neutral|unobtrusive)\s+(?:dialogue\s+tag|speech\s+verb|attribution)\b/i;

const CLICHE_CLAIM = /\bclich[eé]/i;

function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function words(text: string): string[] {
  return text.toLowerCase().match(/[\p{L}\p{N}]+(?:['’][\p{L}\p{N}]+)*/gu) || [];
}

function phraseCount(text: string, phrase: string): number {
  const pattern = escapeRegex(phrase.trim()).replace(/\s+/g, "\\s+");
  return [...text.matchAll(new RegExp(`(?<![\\p{L}\\p{N}])${pattern}(?![\\p{L}\\p{N}])`, "giu"))].length;
}

const QUOTED_TEXT = /“([^”]+)”|‘([^’]+)’|"([^"\n]+)"|(?<![\p{L}\p{N}])'([^'\n]+)'/gu;

function maskQuotes(text: string): string {
  return text.replace(QUOTED_TEXT, match => "x".repeat(match.length));
}

/** Keep independent instructions separate, without splitting quoted prose.
 * Preservation and negative instructions are not requests to change that text. */
function feedbackClauses(comment: string): string[] {
  const boundaries = /[.!?;](?:\s+|$)|,\s*but\s+|\bbut\s+(?=(?:add|insert|remove|replace|keep|do|don['’]t)\b)|\band\s+(?=(?:remove|cut|delete|omit|drop|eliminate|add|insert|move|replace)\b)/gi;
  const clauses: string[] = [];
  let start = 0;
  for (const boundary of maskQuotes(comment).matchAll(boundaries)) {
    clauses.push(comment.slice(start, boundary.index).trim());
    start = boundary.index! + boundary[0].length;
  }
  clauses.push(comment.slice(start).trim());
  return clauses.filter(clause => clause && !/^(?:please\s+)?(?:do\s+not|don['’]t|never|no\s+need\s+to|keep|retain|preserve|leave)\b/i.test(clause));
}

/** Quotes may identify a problem OR an anchor/desired replacement. Only classify
 * explicit removal/replacement targets and phrases described as a problem. */
function quotedProblems(comment: string): string[] {
  const problems: string[] = [];
  for (const match of comment.matchAll(QUOTED_TEXT)) {
    const before = comment.slice(0, match.index).split(/[.!?;]\s*/).pop() || "";
    const after = comment.slice(match.index! + match[0].length);
    const targeted = /\b(?:remove|cut|delete|omit|drop|eliminate|replace|avoid)\s+(?:(?:the|repeated|redundant|word|phrase|clich[eé]d?|said-bookism)\s+)*$/i.test(before);
    const described = /^\s+(?:is|feels?|sounds?)\s+(?:a\s+|an\s+|too\s+)?(?:clich[eé]d?|overused|repeated|redundant|unnecessary|said-bookism)(?![\p{L}\p{N}])/iu.test(after);
    if (targeted || described) problems.push(match.slice(1).find(Boolean)!.trim());
  }
  return problems;
}

/** Equal punctuation counts only prove relocation when the words stay the same.
 * Using neighboring word values mistakes when -> once for a moved comma. With
 * simultaneous wording changes we conservatively leave the comment note-only. */
function punctuationChanged(original: string, suggestion: string, pattern: RegExp): boolean {
  const originalMarks = [...original.matchAll(new RegExp(pattern.source, "g"))];
  const suggestedMarks = [...suggestion.matchAll(new RegExp(pattern.source, "g"))];
  if (originalMarks.length !== suggestedMarks.length) return true;
  const withoutMarks = (text: string) => text.replace(new RegExp(pattern.source, "g"), "");
  // Apostrophes can move inside a word (girl's -> girls'); other punctuation
  // separates words and must not join them when removed for this comparison.
  const lexicalWords = (text: string) => words(text.replace(new RegExp(pattern.source, "g"), mark => /['‘’]/.test(mark) ? "" : " ")).join(" ");
  if (lexicalWords(original) !== lexicalWords(suggestion)) return false;
  const positions = (text: string, matches: RegExpMatchArray[]) => matches.map(m =>
    `${withoutMarks(text.slice(0, m.index)).replace(/\s+/g, "").length}:${m[0]}`,
  ).join("|");
  return positions(original, originalMarks) !== positions(suggestion, suggestedMarks);
}

function punctuationClaims(comment: string) {
  const matches = PUNCTUATION_CLAIM_MAP.flatMap(({ keyword, chars }) =>
    [...maskQuotes(comment).matchAll(new RegExp(keyword.source, "gi"))].map(match => ({
      index: match.index!, end: match.index! + match[0].length, chars,
    })),
  ).sort((a, b) => a.index - b.index || b.end - a.end);
  // "em dash" is one claim, not both an em-dash and a generic-dash claim.
  const claims = matches.filter((m, i) => {
    if (matches.slice(0, i).some(other => other.index <= m.index && other.end >= m.end)) return false;
    const keyword = comment.slice(m.index, m.end).toLowerCase();
    const before = comment.slice(0, m.index);
    const after = comment.slice(m.end);
    // These prose senses do not name punctuation, even after verbs like "add"
    // or "correct". Keep other punctuation requests in the same clause intact.
    if (keyword === "dash" && /^\s+of\b/i.test(after)) return false;
    if (keyword === "period" && (/^[\s-]+appropriate\b/i.test(after) || /\b(?:historical|time)\s+$/i.test(before))) return false;
    return true;
  });
  return claims.map((claim, i) => {
    const before = comment.slice(0, claim.index).split(/[.!?;]/).pop() || "";
    const location = comment.slice(claim.end, claims[i + 1]?.index).split(/[.!?;]/)[0];
    const action = before.match(/\b(?:add|insert|missing|remove|cut|delete|omit|drop|eliminate|move|relocate|place|reposition|replace)\b/gi)?.pop()?.toLowerCase();
    return {
      ...claim,
      location,
      action: action ?? (UNNEEDED_PUNCTUATION.test(before + location) ? "remove" : undefined),
    };
  }).filter(claim => {
    // "Simplify the wording before the comma" uses punctuation as an anchor.
    const before = comment.slice(0, claim.index);
    if (/\b(?:before|after)\s+(?:the\s+)?$/i.test(before)) return false;
    return claim.action || /\b(?:missing|needed|incorrect|misplaced|unnecessary|extra|redundant|wrong|fix|correct|splice)\b/i.test(before + claim.location);
  });
}

function punctuationPositionContradicts(
  location: string, original: string, suggestion: string, pattern: RegExp, action?: string,
): boolean {
  const mark = `(?:${pattern.source})`;
  const originalCount = [...original.matchAll(new RegExp(pattern.source, "g"))].length;
  const suggestedCount = [...suggestion.matchAll(new RegExp(pattern.source, "g"))].length;
  const removing = ["remove", "cut", "delete", "omit", "drop", "eliminate"].includes(action || "");
  const adding = !removing && (["add", "insert", "missing"].includes(action || "") || /\b(?:missing|needed)\b/i.test(location));
  if (adding && suggestedCount < originalCount || removing && suggestedCount > originalCount) return true;
  if (/\b(?:at (?:the )?end|end of (?:the )?sentence)\b/i.test(location)) {
    const hasMark = new RegExp(`${mark}["”’']?\\s*$`).test(suggestion);
    return removing ? hasMark : !hasMark;
  }
  // Only literal anchors: "before and", "after the word X", or a quoted phrase.
  // Descriptions such as "after the introductory clause" need grammatical analysis.
  const anchor = location.match(/\b(before|after)\s+(?:["“‘']([^"”’']+)["”’']|(?:the\s+)?word\s+(\w+)|(?:the\s+)?(and|but|or|nor|for|so|yet|however)\b)/i);
  if (!anchor) return false;
  const phrase = anchor[2] || anchor[3] || anchor[4];
  if (!phraseCount(original, phrase) || !phraseCount(suggestion, phrase)) return false;
  const word = `(?<![\\p{L}\\p{N}])${escapeRegex(phrase)}(?![\\p{L}\\p{N}])`;
  const expression = anchor[1].toLowerCase() === "before" ? `${mark}\\s*${word}` : `${word}\\s*${mark}`;
  const count = (text: string) => [...text.matchAll(new RegExp(expression, "giu"))].length;
  // A pre-existing correct occurrence must not excuse punctuation added elsewhere.
  if (removing) return count(suggestion) >= count(original);
  return count(suggestion) <= count(original);
}

function sentences(text: string): string[] {
  return text.trim().split(/(?<=[.!?])\s+|\n+/).map(s => s.trim()).filter(Boolean);
}

function isSentenceReordering(original: string, suggestion: string): boolean {
  const before = sentences(original);
  const after = sentences(suggestion);
  return before.length > 1 && before.join(" ") !== after.join(" ") &&
    [...before].sort().join("\n") === [...after].sort().join("\n");
}

/** Accept deletion of a complete sentence or words without rephrasing. For a
 * repetition claim, deleted words must remain elsewhere; merely shortening a
 * repeated scene does not remove that scene. */
function hasVerifiableRemoval(original: string, suggestion: string, repeated: boolean): boolean {
  const before = sentences(original);
  const after = sentences(suggestion);
  const remaining = [...before];
  if (after.length < before.length && after.every(sentence => {
    const index = remaining.indexOf(sentence);
    if (index === -1) return false;
    remaining.splice(index, 1);
    return true;
  })) return true;

  const originalWords = words(original);
  const suggestedWords = words(suggestion);
  if (!suggestedWords.length || suggestedWords.length >= originalWords.length) return false;
  let next = 0;
  const removed: string[] = [];
  for (const word of originalWords) {
    if (word === suggestedWords[next]) next++;
    else removed.push(word);
  }
  // Only deleted tokens that remain elsewhere are demonstrably repetition.
  return next === suggestedWords.length && (!repeated || removed.every(word => suggestedWords.includes(word)));
}

function hasMarkedDialogueTag(text: string): boolean {
  const verbs = [...SAID_BOOKISMS].join("|");
  const beforeSpeech = new RegExp(`\\b(?:${verbs})(?:\\s*[:,])?\\s*$`, "iu");
  const afterSpeech = new RegExp(`^\\s+(?:[\\p{L}]+\\s+){0,3}(?:${verbs})\\b`, "iu");
  // Pair opening/closing quotes before inspecting attribution. A verb inside
  // "I cried," is dialogue, not a preposed tag for the closing straight quote.
  let previousEnd = 0;
  for (const speech of text.matchAll(/"[^"]*"|“[^”]*”/gu)) {
    const end = speech.index! + speech[0].length;
    if (beforeSpeech.test(text.slice(previousEnd, speech.index)) || afterSpeech.test(text.slice(end))) return true;
    previousEnd = end;
  }
  return false;
}

/**
 * Bounded deterministic guards for SW-10283, not a semantic validity guarantee.
 * Every applicable guard runs: satisfying punctuation cannot excuse a retained
 * cliché or a second unaddressed punctuation claim. Unrecognized prose issues
 * are left to generation; no model call or API contract change is introduced.
 */
export function suggestionContradictsComment(params: {
  comment: string;
  original: string;
  suggestion: string;
  feedbackType?: string | null;
}): boolean {
  const { comment, original, suggestion, feedbackType } = params;
  if (!comment || !original || !suggestion) return false;

  const clauses = feedbackClauses(comment);
  if (clauses.some(clause => clauseContradictsSuggestion(clause, original, suggestion))) return true;
  // An explicit punctuation mention can be a preservation instruction or an
  // anchor. Do not turn it back into a change request via the plugin label.
  return clauses.length > 0 && !PUNCTUATION_CLAIM_MAP.some(({ keyword }) => keyword.test(comment)) &&
    typeof feedbackType === "string" && feedbackType.toLowerCase().includes("punctuation") &&
    !punctuationChanged(original, suggestion, ALL_PUNCTUATION_PATTERN);
}

function clauseContradictsSuggestion(comment: string, original: string, suggestion: string): boolean {
  // "Out of place" and "takes place" describe prose or setting; they do not
  // instruct us to relocate it. Mask quoted prose for the same reason.
  const moveInstruction = maskQuotes(comment).replace(/\b(?:out\s+of|takes?|took|taken|taking)\s+place\b/gi, "");
  if (MOVE_CLAIM.test(moveInstruction) && !MOVE_PUNCTUATION.test(moveInstruction) && !isSentenceReordering(original, suggestion)) return true;

  const claims = punctuationClaims(comment);
  if (claims.some(({ chars, location, action }) => !punctuationChanged(original, suggestion, chars) ||
    punctuationPositionContradicts(location, original, suggestion, chars, action))) return true;

  const problems = quotedProblems(comment).filter(phrase => phraseCount(original, phrase) > 0);
  for (const phrase of problems) {
    const remaining = phraseCount(suggestion, phrase);
    if (REMOVAL_CLAIM.test(comment) ? remaining >= phraseCount(original, phrase) : remaining > 0) return true;
  }
  const removesPunctuation = claims.some(({ action }) => ["remove", "cut", "delete", "omit", "drop", "eliminate"].includes(action || ""));
  if (REMOVAL_CLAIM.test(comment) && !problems.length && !removesPunctuation &&
    !hasVerifiableRemoval(original, suggestion, /\b(?:repeated|duplicate)\b/i.test(comment))) return true;

  if (SAID_BOOKISM_CLAIM.test(comment) && hasMarkedDialogueTag(original) && hasMarkedDialogueTag(suggestion)) return true;

  // The reported equivalent-problem swap. Do not equate every new simile with a
  // cliché: that suppresses original imagery, which is precisely the requested fix.
  if (CLICHE_CLAIM.test(comment) && /\bsomething fragile\b/i.test(original) && /\blike glass\b/i.test(suggestion)) return true;
  return false;
}
